import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MdAdd, MdEdit, MdDelete, MdSearch, MdPerson } from 'react-icons/md';
import axiosInstance from '../../api/axiosInstance';

const TeamList = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await axiosInstance.get('/team');
        setTeam(response.data.data || []);
      } catch (err) {
        console.error('Failed to fetch team:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTeam();
  }, []);

  const filteredTeam = team.filter((member) =>
    member.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this team member?')) {
      try {
        await axiosInstance.delete(`/team/${id}`);
        const response = await axiosInstance.get('/team');
        setTeam(response.data.data || []);
      } catch (err) {
        alert('Failed to delete team member');
        console.error('Delete error:', err);
      }
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Team Members</h1>
        <Link
          to="/admin/team/create"
          className="flex items-center justify-center gap-2 bg-[#008300] text-white px-4 py-2 rounded-lg hover:bg-[#006600] transition-colors text-sm sm:text-base"
        >
          <MdAdd />
          <span>Add Team Member</span>
        </Link>
      </div>

      {/* Search */}
      <div className="mb-4 sm:mb-6">
        <div className="relative">
          <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search team members..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008300] focus:border-transparent outline-none text-sm sm:text-base"
          />
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-12 text-gray-500 text-sm sm:text-base">Loading team members...</div>
      )}

      {/* Cards Grid */}
      {!loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {filteredTeam.map((member) => (
            <div
              key={member.id}
              className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow overflow-hidden flex flex-col"
            >
              {/* ── Photo area ── */}
              <div className="relative h-48 sm:h-52 bg-gray-100 flex-shrink-0">
                {member.image ? (
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                {/* Fallback placeholder */}
                <div
                  className="absolute inset-0 bg-gray-100 items-center justify-center"
                  style={{ display: member.image ? 'none' : 'flex' }}
                >
                  <MdPerson className="text-5xl sm:text-6xl text-gray-300" />
                </div>
              </div>

              {/* ── Info ── */}
              <div className="p-3 sm:p-4 flex flex-col flex-1">
                <h3 className="text-sm sm:text-base font-bold text-gray-800 text-center leading-tight mb-1">
                  {member.name}
                </h3>

                {member.role && (
                  <p className="text-[#008300] text-xs font-semibold uppercase tracking-wider text-center mb-0.5">
                    {member.role}
                  </p>
                )}

                {member.designation && (
                  <p className="text-gray-400 text-xs text-center mb-3">
                    {member.designation}
                  </p>
                )}

                {/* Actions */}
                <div className="flex justify-center gap-2 sm:gap-3 mt-auto pt-3 border-t border-gray-100">
                  <Link
                    to={`/admin/team/edit/${member.id}`}
                    className="flex items-center gap-1 px-2 sm:px-3 py-1.5 text-xs sm:text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium"
                    title="Edit"
                  >
                    <MdEdit className="text-sm sm:text-base" />
                    Edit
                  </Link>
                  <button
                    onClick={() => handleDelete(member.id)}
                    className="flex items-center gap-1 px-2 sm:px-3 py-1.5 text-xs sm:text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium"
                    title="Delete"
                  >
                    <MdDelete className="text-sm sm:text-base" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}

          {filteredTeam.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-500 text-sm sm:text-base">
              No team members found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamList;
